import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * 通知弹窗 —— 运维平台「公告管理」打通（P2）：
 * 用户菜单「通知」入口打开，列出发布中且在投放期内的公告
 * （GET /api/announcements，公开接口；失败/空列表回落空态兜底）。
 * 层级 z-[260]：会员页（250）之上、登录弹窗（300）之下。
 */

/** 公告类型 → 展示标签/配色（与运维平台公告管理枚举对齐，未知类型回落「公告」） */
const MSG_TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
  notice: { label: '公告', color: '#0EA1F0', bg: 'rgba(14,161,240,0.12)' },
  activity: { label: '活动', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  urgent: { label: '紧急', color: '#FD475F', bg: 'rgba(253,71,95,0.12)' },
  maintenance: { label: '维护', color: '#A1A1AA', bg: 'rgba(161,161,170,0.12)' },
};

interface AnnouncementItem {
  id: string;
  title: string;
  content: string;
  msgType: string;
  startTime: string | null;
  endTime: string | null;
}

/** 投放开始时间 → 本地化展示（无效/缺失时间不渲染时间行） */
const formatTime = (iso: string | null): string => {
  if (!iso) return '';
  const t = new Date(iso);
  return Number.isNaN(t.getTime())
    ? ''
    : t.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const AnnouncementsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  // null=加载中（区别于拉取完成后的空列表），展示骨架占位
  const [items, setItems] = useState<AnnouncementItem[] | null>(null);

  // 公告拉取：组件实例级竞态防护（disposed），失败静默回落空态
  useEffect(() => {
    let disposed = false;
    fetch('/api/announcements')
      .then((res) => (res.ok ? res.json() : []))
      .then((rows) => {
        if (!disposed) setItems(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!disposed) setItems([]);
      });
    return () => {
      disposed = true;
    };
  }, []);

  // ESC 关闭
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[260] flex items-center justify-center p-4 animate-in fade-in duration-150"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="flex max-h-[72vh] w-full max-w-[560px] flex-col rounded-2xl animate-in zoom-in-95 duration-150"
        style={{ backgroundColor: '#181818', border: '1px solid #2D2D2D' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部：标题 + 关闭 */}
        <div className="flex shrink-0 items-center justify-between px-6 pb-4 pt-5" style={{ borderBottom: '1px solid #2D2D2D' }}>
          <div className="text-base font-semibold text-white">通知</div>
          <button onClick={onClose} aria-label="关闭" className="text-zinc-500 transition-colors hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* 列表：加载骨架 → 空态 / 公告卡片流 */}
        <div className="min-h-[160px] flex-1 overflow-y-auto px-6 py-4">
          {items === null && (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-xl" style={{ backgroundColor: '#222222' }} />
              ))}
            </div>
          )}
          {items !== null && items.length === 0 && (
            <div className="flex h-[160px] items-center justify-center text-sm text-zinc-500">暂无新通知</div>
          )}
          {items !== null && items.length > 0 && (
            <div className="space-y-3">
              {items.map((item) => {
                const meta = MSG_TYPE_META[item.msgType] ?? MSG_TYPE_META.notice;
                const time = formatTime(item.startTime);
                return (
                  <div key={item.id} className="rounded-xl p-4" style={{ backgroundColor: '#222222' }}>
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 rounded px-1.5 py-0.5 text-[11px] leading-none" style={{ color: meta.color, backgroundColor: meta.bg }}>
                        {meta.label}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{item.title}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-line break-words text-[13px] leading-5" style={{ color: 'rgba(255,255,255,0.65)' }}>
                      {item.content}
                    </p>
                    {time && (
                      <div className="mt-2 text-[12px] leading-none" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        {time}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
